import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import AnalysisModule from '../components/AnalysisModule';
import ExpandableCard from '../components/ExpandableCard';
import History from '../components/History';
import { initFirebase, saveAnalysis, subscribeToHistory } from '../firebase';

const Mackinder03 = () => {
  const [userId, setUserId] = useState(null);
  const [authStatus, setAuthStatus] = useState('Cargando autenticación...');
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    const init = async () => {
      const uid = await initFirebase();
      if (uid) {
        setUserId(uid);
        setAuthStatus(`✅ Conectado`);
        
        const unsubscribe = subscribeToHistory(uid, (data) => {
          setAnalyses(data);
        });
        
        return () => unsubscribe();
      } else {
        setAuthStatus('⚠️ No hay usuario autenticado');
      }
    };
    
    init();
  }, []);

  const handleSave = (query, result, type) => {
    if (userId) {
      saveAnalysis(userId, query, result, type);
    }
  };

  return (
    <div className="container">
      <Header 
        title="Mackinder - Parte 3" 
        subtitle="Críticas y Evolución de la Teoría"
        authStatus={authStatus}
      />

      <section className="card" style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004e92', marginBottom: '1rem' }}>
          Aplicación Histórica: El Siglo XX
        </h2>
        <p style={{ lineHeight: '1.7', maxWidth: '800px', margin: '0 auto' }}>
          La teoría de Mackinder no fue solo académica: influyó directamente en las estrategias geopolíticas de las grandes potencias del siglo XX.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '2rem', color: '#004e92' }}>
          Casos Históricos del Heartland
        </h3>
        <div className="grid">
          <ExpandableCard
            icon="🔴"
            title="Alemania Nazi (1939-1945)"
            color="#ef4444"
            summary="Lebensraum: La doctrina del 'espacio vital' alemán estaba directamente inspirada en Mackinder."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Cronología de la expansión:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ padding: '0.5rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.25rem' }}>
                    <strong>1939:</strong> Invasión de Polonia - Primera fase del control de Europa del Este
                  </div>
                  <div style={{ padding: '0.5rem', backgroundColor: 'rgba(239, 68, 68, 0.15)', borderRadius: '0.25rem' }}>
                    <strong>1940-41:</strong> Conquista de Europa Occidental y los Balcanes
                  </div>
                  <div style={{ padding: '0.5rem', backgroundColor: 'rgba(239, 68, 68, 0.2)', borderRadius: '0.25rem' }}>
                    <strong>1941:</strong> Operación Barbarroja - Intento de conquistar el Heartland soviético
                  </div>
                  <div style={{ padding: '0.5rem', backgroundColor: 'rgba(239, 68, 68, 0.25)', borderRadius: '0.25rem' }}>
                    <strong>1945:</strong> Derrota total - El Heartland demostró ser inconquistable
                  </div>
                </div>
                <p style={{ marginTop: '1rem', fontStyle: 'italic', color: '#94a3b8' }}>
                  💀 Resultado: 27 millones de soviéticos muertos, pero el Heartland resistió y contraatacó hasta Berlín.
                </p>
              </div>
            }
          />

          <ExpandableCard
            icon="🔵"
            title="Guerra Fría (1947-1991)"
            color="#3b82f6"
            summary="Contención: EE.UU. aplicó la teoría de Mackinder al revés, evitando que la URSS expandiera su control."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Estrategia de Contención:</p>
                <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li><strong>OTAN (1949):</strong> Alianza militar para contener la expansión soviética en Europa</li>
                  <li><strong>Plan Marshall (1948):</strong> Reconstrucción económica de Europa Occidental</li>
                  <li><strong>Alianzas en el Rimland:</strong> Japón, Corea del Sur, Taiwán, Turquía</li>
                  <li><strong>Doctrina Truman:</strong> Apoyo a países amenazados por el comunismo</li>
                </ul>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #22c55e' }}>
                  <p style={{ fontWeight: 'bold', color: '#22c55e' }}>✓ Éxito de la Estrategia</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    La URSS colapsó en 1991 sin lograr expandirse más allá del Heartland. La contención funcionó.
                  </p>
                </div>
              </div>
            }
          />

          <ExpandableCard
            icon="🟣"
            title="Rusia Post-Soviética (1991-Presente)"
            color="#8b5cf6"
            summary="Resurgimiento: Rusia busca recuperar influencia en su 'extranjero cercano' y mantener control del Heartland."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Conflictos por el Heartland:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem' }}>
                    <p style={{ fontWeight: 'bold' }}>🇬🇪 Guerra de Georgia (2008)</p>
                    <p style={{ fontSize: '0.9rem' }}>Rusia invadió Georgia para evitar su acercamiento a la OTAN y mantener influencia en el Cáucaso.</p>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.15)', borderRadius: '0.5rem' }}>
                    <p style={{ fontWeight: 'bold' }}>🇺🇦 Crimea (2014)</p>
                    <p style={{ fontSize: '0.9rem' }}>Anexión de Crimea para asegurar acceso al Mar Negro y evitar expansión de la OTAN.</p>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.2)', borderRadius: '0.5rem' }}>
                    <p style={{ fontWeight: 'bold' }}>🇺🇦 Conflicto en Ucrania (2014-presente)</p>
                    <p style={{ fontSize: '0.9rem' }}>Ucrania es vista como la "puerta" al Heartland. Su control es crucial para Rusia.</p>
                  </div>
                </div>
                <p style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(234, 179, 8, 0.1)', borderRadius: '0.5rem' }}>
                  ⚠️ <strong>Mackinder sigue vigente:</strong> El control de Europa del Este continúa siendo el punto de fricción geopolítica más importante del siglo XXI.
                </p>
              </div>
            }
          />
        </div>
      </section>

      <section className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#004e92', textAlign: 'center' }}>
          🎯 Lecciones del Siglo XX
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.5rem', borderLeft: '4px solid #22c55e' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#22c55e' }}>✓ Validación</p>
            <p style={{ fontSize: '0.9rem' }}>Europa del Este sigue siendo crucial para el equilibrio de poder global</p>
          </div>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(234, 179, 8, 0.1)', borderRadius: '0.5rem', borderLeft: '4px solid #eab308' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#eab308' }}>⚠️ Limitación</p>
            <p style={{ fontSize: '0.9rem' }}>El poder naval y aéreo pueden contrarrestar el control terrestre</p>
          </div>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', borderLeft: '4px solid #3b82f6' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#3b82f6' }}>💡 Evolución</p>
            <p style={{ fontSize: '0.9rem' }}>La tecnología moderna ha cambiado la naturaleza del poder geopolítico</p>
          </div>
        </div>
      </section>

      <AnalysisModule
        title="✨ Análisis Crítico"
        description="Explore las críticas y limitaciones de la teoría del Heartland."
        inputType="textarea"
        systemPrompt="Actúa como un académico crítico de geopolítica. Analiza las limitaciones y críticas de la Teoría del Heartland de Mackinder. Considera factores como tecnología, poder naval, y cambios en el orden mundial. Responde en español de forma concisa."
        onSave={handleSave}
        type="section"
      />

      <History analyses={analyses} />

      <div style={{ position: 'fixed', bottom: '20px', right: '20px', display: 'flex', gap: '10px', zIndex: 1000 }}>
        <Link to="/mackinder02" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          ⬅️ Anterior
        </Link>
        <Link to="/" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          🏠 Inicio
        </Link>
        <Link to="/mackspyman04" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          Siguiente ➡️
        </Link>
      </div>
    </div>
  );
};

export default Mackinder03;
